import { useQuery } from '@tanstack/react-query'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import LinkText from 'ext-ui/components/LinkText/LinkText'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import AlertTriangleYellowIcon from 'ext-ui/svg/icon/AlertTriangleYellowIcon'
import { Moment } from 'moment-timezone'
import React, { useState } from 'react'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import { useTranslate } from 'src/localization/Trns'
import { bankAccountsUrl, vendorsUrl } from 'src/navigation/urls'
import { paymentsQueries } from 'src/queries/payments/payments.queries'
import { vendorQueries } from 'src/queries/vendors/vendors.queries'
import { Vendor } from 'src/queries/vendors/vendors.types'

import { BILL_PAYMENT_ACKNOWLEDGEMENT } from '../billPaymentForm'
import { footer, footerButtons } from '../PayBillFlyout.style'
import {
  ackCopy,
  ackRow,
  alertContent,
  alertTitle,
  alertWrap,
  card,
  cardBody,
  cardTitle,
  hero,
  heroAmount,
  heroLabel,
  heroVendor,
  reviewScroll,
  row,
  rowDivider,
  rowLabel,
  rowList,
  rowValue,
} from './ReviewStep.style'

export interface ReviewStepProps {
  organizationId: string
  vendor: Vendor | null
  amount: number | undefined
  invoiceNumber: string
  invoiceDate: Moment | undefined
  dueDate: Moment | undefined
  mode:
    | 'editable'
    | 'confirming'
    | 'confirm-unknown'
    | 'sealed'
    | 'reconciling'
    | 'terminal'
  errorMessage?: string
  onBack: () => void
  onConfirm: () => void
}

/** Every missing value in every row renders as an em-dash. */
const EM_DASH = '—'

/**
 * Compact, display-safe bank identity: bank name + masked last-4 (e.g.
 * `Chase •••• 4321`). Matches the app's established masking (four U+2022
 * bullets + one space — BillPayBankAccounts.tsx). Falls back to the masked
 * number alone when the bank name is missing, and to an em-dash when neither is
 * present. Every field off these DTOs is nullable, so this never renders
 * `undefined`, `null`, or an empty string.
 */
const formatBankIdentity = (
  bankName: string | null | undefined,
  accountNumberLast4: string | null | undefined,
): string => {
  const name = bankName?.trim() ?? ''
  const masked = accountNumberLast4?.trim()
    ? `•••• ${accountNumberLast4.trim()}`
    : ''
  if (name && masked) return `${name} ${masked}`
  if (masked) return masked
  if (name) return name
  return EM_DASH
}

/** ACH is USD-only (see the AmountInputField `currency` note in Step 1). */
const formatAmount = (amount: number | undefined): string =>
  amount === undefined
    ? EM_DASH
    : `$${amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`

const formatDate = (date: Moment | undefined): string =>
  date ? date.format('MMM D, YYYY') : EM_DASH

/** "Active" org funding account = a linked, usable (VERIFIED) account. Mirrors
 * the prototype's `activeOrgAccounts.length === 0` gate against our single
 * funding-account model. Anything else (none, pending, unverified) leaves the
 * payment ungated-but-blocked and surfaces the no-account alert. */
const isActiveFundingAccount = (
  details: { status?: string | null } | undefined,
): boolean => (details?.status ?? '').toUpperCase() === 'VERIFIED'

/** HTTP status off a normalized `QueryError` (`network.utils.normalizeQueryError`
 * carries it at the top level). Ad-hoc because the repo has no shared helper —
 * same read `transactionActivities.queries.ts` does. */
const statusOf = (error: unknown): number | undefined =>
  error && typeof error === 'object' && 'status' in error
    ? (error as { status?: number }).status
    : undefined

/**
 * A bank-identity row value: hyperlinked when there is a real identity to
 * show, plain text when there isn't. Deliberately NOT a link in the fallback
 * case — the prototype makes the same call ("'—' isn't a link to anywhere"),
 * and a hyperlinked em-dash reads as a value the user can act on when the
 * whole point of the em-dash is that there is nothing there.
 */
const BankIdentity: React.FC<{
  bankName: string | null | undefined
  accountNumberLast4: string | null | undefined
  href: string
}> = ({ bankName, accountNumberLast4, href }) => {
  const identity = formatBankIdentity(bankName, accountNumberLast4)
  if (identity === EM_DASH) return <>{identity}</>
  return <LinkText href={href}>{identity}</LinkText>
}

const ReviewRow: React.FC<
  React.PropsWithChildren<{
    label: string
    tooltip?: string
    tooltipTestId?: string
    valueTestId?: string
  }>
> = ({ label, tooltip, tooltipTestId, valueTestId, children }) => (
  <div css={row}>
    <span css={rowLabel}>
      {label}
      {tooltip ? (
        <TooltipIcon iconTestId={tooltipTestId} placement={'top'}>
          {tooltip}
        </TooltipIcon>
      ) : null}
    </span>
    <span css={rowValue} data-testid={valueTestId}>
      {children}
    </span>
  </div>
)

/**
 * Pay Bill flyout, Step 2 (Review and Pay) — Bill Pay M3.1 (EX-40563).
 *
 * A clear summary of the payment before it is submitted, plus the required
 * acknowledgement. The parent owns confirmation and authorization state; this
 * component turns that state into a deliberately restricted Review surface.
 */
const ReviewStep: React.FC<ReviewStepProps> = ({
  organizationId,
  vendor,
  amount,
  invoiceNumber,
  invoiceDate,
  dueDate,
  mode,
  errorMessage,
  onBack,
  onConfirm,
}) => {
  const translate = useTranslate()
  const [acknowledged, setAcknowledged] = useState(false)

  // Step 1 only lets a PAYABLE vendor through, so a payment method is expected —
  // but the id is still read defensively (the query stays disabled without it).
  const paymentMethodId = vendor?.paymentMethod?.id

  const vendorBankQuery = useQuery(
    vendorQueries.paymentMethodDetails(
      organizationId,
      vendor?.id,
      paymentMethodId,
    ),
  )
  const fundingAccountQuery = useQuery({
    ...paymentsQueries.fundingAccount(organizationId),
    /**
     * A 404 from `/payments/funding-account` is this endpoint's way of saying
     * "no funding account on file" — a definitive answer, not a transient
     * failure. The client sets no global `retry`, so the default 3 attempts +
     * exponential backoff kept `isLoading` true for ~12s (measured against
     * stage on an org with no account): the review screen sat there with a
     * disabled `Confirm and Pay` and no alert explaining why. The alert exists
     * precisely to explain that disabled button, so it must land with it.
     * Same 404-as-signal override the rest of `src/queries` already uses
     * (`transactionActivities.queries.ts`, `expenseReports.queries.ts`,
     * `useQueryCardFeed.ts`); scoped to this consumer so M1's Bank Accounts
     * page keeps the shared factory's behaviour unchanged.
     */
    retry: (failureCount: number, error: unknown) =>
      statusOf(error) !== 404 && failureCount < 2,
  })

  const vendorBank = vendorBankQuery.data
  const fundingAccount = fundingAccountQuery.data

  const hasReviewableVendorBank =
    formatBankIdentity(vendorBank?.bankName, vendorBank?.accountNumberLast4) !==
    EM_DASH
  const vendorBankIsResolving =
    vendorBankQuery.isLoading || vendorBankQuery.isFetching
  const vendorBankReady =
    !vendorBankIsResolving &&
    !vendorBankQuery.isError &&
    hasReviewableVendorBank
  const showVendorBankError = !vendorBankIsResolving && !vendorBankReady
  const hasActiveFundingAccount = isActiveFundingAccount(fundingAccount)

  /**
   * "We couldn't read it" is NOT "there isn't one." A 404 is this endpoint's
   * answer for "no funding account on file"; anything else that fails (a 500,
   * a network drop) tells us nothing about whether the org has an account. In
   * that case pointing the user at `Add bank account` would be actively wrong
   * — they may well already have one — so the two states get different alerts.
   * `Confirm and Pay` stays gated either way: we can't confirm an ACH payment
   * we can't confirm a funding account for.
   */
  const fundingReadFailed =
    fundingAccountQuery.isError && statusOf(fundingAccountQuery.error) !== 404
  // Don't flash either alert while the funding read is still resolving.
  const showNoFundingAlert =
    !fundingAccountQuery.isLoading &&
    !fundingReadFailed &&
    !hasActiveFundingAccount
  const showFundingErrorAlert =
    !fundingAccountQuery.isLoading && fundingReadFailed

  // The acknowledgement copy doubles as the checkbox's accessible name.
  const acknowledgementCopy = translate(BILL_PAYMENT_ACKNOWLEDGEMENT.copy)

  const acknowledgementChecked = mode === 'editable' ? acknowledged : true
  const acknowledgementDisabled = mode !== 'editable'
  // The funding-account gate only applies before the first confirmation.
  const confirmDisabled =
    mode === 'editable'
      ? !acknowledged || !hasActiveFundingAccount || !vendorBankReady
      : mode === 'confirming'
  const primaryLabel =
    mode === 'confirming'
      ? translate('Confirming payment')
      : mode === 'confirm-unknown'
        ? translate('Retry confirmation')
        : mode === 'sealed'
          ? translate('Continue verification')
          : translate('Confirm and Pay')
  const terminalMessage =
    errorMessage ??
    translate('The payment authorization is no longer available.')

  return (
    <>
      <div css={reviewScroll}>
        {showVendorBankError && (
          <AlertPanel
            variant={'severe'}
            role={'alert'}
            data-testid={'pay-bill-vendor-bank-error'}
          >
            {translate(
              "We couldn't load this vendor's bank account. Try again before confirming.",
            )}
          </AlertPanel>
        )}

        {showFundingErrorAlert && (
          /* Reuses M1's own funding-details failure treatment verbatim
             (`BillPayBankAccounts.tsx` — severe AlertPanel, same string), so
             the two Bill Pay surfaces report the same failure the same way and
             no new copy is invented for it. */
          <AlertPanel
            variant={'severe'}
            role={'alert'}
            data-testid={'pay-bill-funding-error'}
          >
            {translate('Something went wrong. Please try again.')}
          </AlertPanel>
        )}

        {showNoFundingAlert && (
          <div css={alertWrap}>
            <AlertPanel variant={'warning'} icon={<AlertTriangleYellowIcon />}>
              <span css={alertContent}>
                <span css={alertTitle}>
                  {translate('No Bill Payer Bank Account on file')}
                </span>
                <span>
                  {translate(
                    "This is your organization's own bank account — the one this ACH payment will be sent from. Add one to confirm and send this payment.",
                  )}
                </span>
              </span>
            </AlertPanel>
            {/* Full-page navigation, no router import (the page-owns-routing
                rule). Matches Step 1's plain-anchor convention. */}
            <Button
              variant={'primary'}
              type={'button'}
              onClick={() =>
                window.location.assign(bankAccountsUrl(organizationId))
              }
              data-testid={'pay-bill-add-bank-account'}
            >
              {translate('Add bank account')}
            </Button>
          </div>
        )}

        {mode === 'confirming' && (
          <AlertPanel role={'status'} data-testid={'pay-bill-confirming'}>
            {translate('Confirming payment')}
          </AlertPanel>
        )}

        {mode === 'reconciling' && (
          <AlertPanel role={'status'} data-testid={'pay-bill-reconciling'}>
            {translate('Checking payment status')}
          </AlertPanel>
        )}

        {(mode === 'confirm-unknown' ||
          mode === 'terminal' ||
          (errorMessage && mode !== 'reconciling')) && (
          <AlertPanel
            variant={mode === 'confirm-unknown' ? 'warning' : 'severe'}
            role={'alert'}
            data-testid={'pay-bill-confirmation-error'}
          >
            {terminalMessage}
          </AlertPanel>
        )}

        <div css={hero} data-testid={'pay-bill-review-hero'}>
          <span css={heroLabel}>{translate("You're paying")}</span>
          <span css={heroAmount} data-testid={'pay-bill-review-amount'}>
            {formatAmount(amount)}
          </span>
          <span css={heroVendor} data-testid={'pay-bill-review-vendor'}>
            {vendor?.name?.trim() ? vendor.name : translate('Untitled Vendor')}
          </span>
        </div>

        <div css={card}>
          <div css={cardBody}>
            <h2 css={cardTitle}>{translate('Review ACH Payment Details')}</h2>
            <div css={rowList}>
              <ReviewRow
                label={translate('Vendor Bank Account')}
                tooltip={translate(
                  "The vendor's bank account this payment will be sent to.",
                )}
                tooltipTestId={'pay-bill-review-tooltip-vendor-bank'}
                valueTestId={'pay-bill-review-vendor-bank'}
              >
                <BankIdentity
                  bankName={vendorBank?.bankName}
                  accountNumberLast4={vendorBank?.accountNumberLast4}
                  href={vendorsUrl()}
                />
              </ReviewRow>
              <div css={rowDivider} />

              <ReviewRow
                label={translate('Bill Payer Bank Account')}
                tooltip={translate(
                  "Your organization's bank account this payment will be sent from.",
                )}
                tooltipTestId={'pay-bill-review-tooltip-payer-bank'}
                valueTestId={'pay-bill-review-payer-bank'}
              >
                <BankIdentity
                  bankName={fundingAccount?.bankName}
                  accountNumberLast4={fundingAccount?.accountNumberLast4}
                  href={bankAccountsUrl(organizationId)}
                />
              </ReviewRow>
              <div css={rowDivider} />

              <ReviewRow
                label={translate('Vendor Remittance Email')}
                tooltip={translate(
                  "Where we'll send the payment notification and remittance details.",
                )}
                tooltipTestId={'pay-bill-review-tooltip-email'}
                valueTestId={'pay-bill-review-email'}
              >
                {vendor?.email?.trim() ? vendor.email : EM_DASH}
              </ReviewRow>
              <div css={rowDivider} />

              <ReviewRow
                label={translate('Invoice Number')}
                valueTestId={'pay-bill-review-invoice-number'}
              >
                {invoiceNumber.trim() ? invoiceNumber : EM_DASH}
              </ReviewRow>
              <div css={rowDivider} />

              <ReviewRow
                label={translate('Invoice Date')}
                valueTestId={'pay-bill-review-invoice-date'}
              >
                {formatDate(invoiceDate)}
              </ReviewRow>
              <div css={rowDivider} />

              {/* "Due Date" verbatim from the Step 1 field this value comes
                  from, and from `invoiceDueDate` on the wire — a review screen
                  reads back what the user entered, in their words. Ordered
                  last to match Step 1's collection order: number, invoice
                  date, due date. */}
              <ReviewRow
                label={translate('Due Date')}
                valueTestId={'pay-bill-review-due-date'}
              >
                {formatDate(dueDate)}
              </ReviewRow>
            </div>
          </div>
        </div>
      </div>

      {/* The acknowledgement is pinned in the sticky footer (design
          instruction), not at the bottom of the scrolling body. */}
      <div css={footer}>
        <label css={ackRow}>
          <Checkbox
            checked={acknowledgementChecked}
            disabled={acknowledgementDisabled}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setAcknowledged(event.target.checked)
            }
            aria-label={acknowledgementCopy}
            data-testid={'pay-bill-acknowledge'}
          />
          <span css={ackCopy}>{acknowledgementCopy}</span>
        </label>
        <div css={footerButtons}>
          {mode === 'editable' && (
            <Button
              // `secondary` + a leading chevron, matching the prototype's
              // `variant="secondary" iconLeft={<ChevronLeftIcon />}`. Step 1's
              // Cancel is deliberately `no-shell` (a direct design instruction
              // scoped to Step 1); Step 2's Go back is the bordered secondary —
              // the base branch's placeholder `Back` had inherited the wrong one.
              // `currentColor` for the same reason Step 1's Next needs it: the
              // Svg defaults its fill to the CTA blue rather than the button's.
              variant={'secondary'}
              type={'button'}
              onClick={onBack}
              iconLeft={<SvgChevronLeft fill={'currentColor'} />}
              data-testid={'pay-bill-back'}
            >
              {translate('Go back')}
            </Button>
          )}
          {mode !== 'terminal' && mode !== 'reconciling' && (
            <Button
              variant={'primary'}
              type={'button'}
              disabled={confirmDisabled}
              onClick={onConfirm}
              data-testid={'pay-bill-confirm'}
            >
              {primaryLabel}
            </Button>
          )}
        </div>
      </div>
    </>
  )
}

export default ReviewStep
